import { camelise } from '../camelise';
import { dateFormat } from '../dates';

export const multipleValueToString = (arrayValue) => {
  if (arrayValue.length < 2) return arrayValue.join();

  const arrayValueString = arrayValue.join();
  const lastComma = arrayValueString.lastIndexOf(',');
  const BeginingString = arrayValueString.substring(0, lastComma);
  const endString = arrayValueString.substring(lastComma + 1, arrayValueString.length);

  return `${BeginingString} and ${endString}`;
};

const fieldTypeValueFormatter = (field) => {
  const { type, value } = field;

  switch (type) {
    case 'date':
      return dateFormat(value, { withoutTime: false });
    case 'checkbox':
      return multipleValueToString(value);
    case 'radio': {
      if (field.other_enabled && Boolean(value) && !field.options.includes(value)) {
        const otherField = field.other_field ?? 'Other';
        return `${otherField}: ${value}`;
      }

      return value;
    }
    default:
      return value;
  }
};

const prepareHistoryHeaders = (formHistory) => {
  const preparedHistoryColumns = [];

  formHistory?.forEach((history) => {
    const userAccessor = `${camelise(history.transition_activity_text)}User`;
    const transitionDateAccessor = `${camelise(history.transition_activity_text)}Date`;
    const usersNotifiedAccessor = `${camelise(history.transition_activity_text)}UsersNotified`;
    const notifiedDateAccessor = `${camelise(history.transition_activity_text)}NotifiedDate`;
    const reasonAccessor = `${camelise(history.transition_activity_text)}ReasonForChange`;

    preparedHistoryColumns.push({
      Header: `${history.transition_activity_text} by`,
      accessor: userAccessor,
    });

    preparedHistoryColumns.push({
      Header: `${history.transition_activity_text} Date`,
      accessor: transitionDateAccessor,
    });

    preparedHistoryColumns.push({
      Header: `Notified for ${history.transition_activity_text}`,
      accessor: usersNotifiedAccessor,
    });

    preparedHistoryColumns.push({
      Header: `Notified Date ${history.transition_activity_text}`,
      accessor: notifiedDateAccessor,
    });

    preparedHistoryColumns.push({
      Header: `${history.transition_activity_text} Reason for Change`,
      accessor: reasonAccessor,
    });
  });
  return preparedHistoryColumns;
};

const prepareHistoryData = (history) => {
  const historyData = {};

  history.forEach((historyInstance) => {
    const userAccessor = `${camelise(historyInstance.transition_activity_text)}User`;
    const transitionDateAccessor = `${camelise(historyInstance.transition_activity_text)}Date`;
    const usersNotifiedAccessor = `${camelise(historyInstance.transition_activity_text)}UsersNotified`;
    const notifiedDateAccessor = `${camelise(historyInstance.transition_activity_text)}NotifiedDate`;
    const reasonAccessor = `${camelise(historyInstance.transition_activity_text)}ReasonForChange`;
    historyData[userAccessor] = `${historyInstance.user_first_name} ${historyInstance.user_last_name}`;
    historyData[transitionDateAccessor] = dateFormat(historyInstance.created_at, {
      timezone: 'utc',
      withoutTime: false,
    });
    historyData[usersNotifiedAccessor] = historyInstance?.users_notified.map((user) => userFullName(user))?.join(', ');
    historyData[notifiedDateAccessor] =
      historyInstance?.users_notified.length > 0
        ? dateFormat(historyInstance.created_at, { timezone: 'utc', withoutTime: false })
        : '';
    historyData[reasonAccessor] = historyInstance.reason_for_change ? historyInstance.reason_for_change : '';
  });

  return historyData;
};

export const prepareStaticColumnHeaders = (versionHistory, options = {}) => {
  const staticHeadersOne = [
    { Header: 'Form ID', accessor: 'formId' },
    { Header: 'Form Version', accessor: 'formVersion' },
    { Header: 'Current State', accessor: 'formState' },
    { Header: 'Date Created', accessor: 'createdAt.text' },
    { Header: 'Created By', accessor: 'user' },
    ...prepareHistoryHeaders(versionHistory),
    { Header: 'Form Template ID', accessor: 'formTemplateId' },
    { Header: 'Form Template Version', accessor: 'formTemplateVersion' },
    { Header: 'Trial', accessor: 'trial' },
    { Header: 'Library/Site Name', accessor: 'libraryName' },
  ];

  if (options.sitesEnabled) {
    staticHeadersOne.push({ Header: 'Subject', accessor: 'subject' });
    staticHeadersOne.push({ Header: 'Study Event', accessor: 'studyEvent' });
    staticHeadersOne.push({ Header: 'Study Procedure', accessor: 'studyProcedure' });
  }

  const staticHeadersTwo = [
    { Header: 'Asset ID', accessor: 'assetId' },
    { Header: 'Asset Upload Date', accessor: 'assetUploadDate.text' },
    { Header: 'Asset Title', accessor: 'assetTitle' },
    { Header: 'Asset Link', accessor: 'assetLink' },
  ];

  return [...staticHeadersOne, ...staticHeadersTwo];
};

export const prepareStaticRowData = (data, options = {}) => {
  const staticDataOne = {
    formId: (
      <a href={data.form_link} target="_blank" rel="noreferrer">
        {data.id}
      </a>
    ),
    formVersion: data.version_number === 0 ? 'DRAFT' : `${data.version_number}`,
    formState: data.current_state?.name,
    createdAt: { text: dateFormat(data.created_at, { timezone: 'utc', withoutTime: false }), value: data.created_at },
    user: userFullName(data.user),
    formTemplateId: data.form_template?.id,
    formTemplateVersion: data.form_template_version,
    trial: data.trial_name,
    libraryName: data.library_name,
  };

  if (options.sitesEnabled) {
    staticDataOne.subject = data.subject;
    staticDataOne.studyEvent = data.study_event;
    staticDataOne.studyProcedure = data.study_procedure;
  }

  const staticDataTwo = {
    assetId: (
      <a href={data.asset_link} target="_blank" rel="noreferrer">
        {data.asset_id}
      </a>
    ),
    assetUploadDate: {
      text: dateFormat(data.asset_upload_date, { timezone: 'utc', withoutTime: false }),
      value: data.asset_upload_date,
    },
    assetTitle: data.asset_title,
    assetLink: (
      <a href={data.asset_link} target="_blank" rel="noreferrer">
        {data.asset_link}
      </a>
    ),
    ...prepareHistoryData(data.version_history),
  };

  return { ...staticDataOne, ...staticDataTwo };
};

export const processData = (currentFormTemplateForms, options = {}) => {
  const { version_history: firstFormHistory } = currentFormTemplateForms[0] || {};
  const dataReadyForTable = [];
  const headersReadyForTable = prepareStaticColumnHeaders(firstFormHistory, { sitesEnabled: options.sitesEnabled });

  currentFormTemplateForms.forEach((form, index) => {
    const firstForm = index === 0;
    const row = prepareStaticRowData(form, { sitesEnabled: options.sitesEnabled });

    form.fields.forEach((field) => {
      if (firstForm)
        headersReadyForTable.push({
          Header: field.enabled ? field.title : `${field.title} (Disabled)`,
          accessor: camelise(field.id),
        });
      row[camelise(field.id)] = fieldTypeValueFormatter(field);
    });

    row.formData = form;
    dataReadyForTable.push(row);
  });
  return { dataReadyForTable, headersReadyForTable };
};

function userFullName(user) {
  if (!user) return '';

  return `${user.first_name} ${user.last_name}`;
}

export const hasValue = (value) => {
  return value !== undefined && value !== '' && value !== null;
};
